// services/chatStream/sseFrameParser.ts

/** One decoded SSE frame: the `event:` name and the joined `data:` payload. */
export interface SseFrame {
  event: string;
  data: string;
}

export interface SseFrameParser {
  /** Feed a decoded chunk; returns the frames it completed. */
  push: (chunk: string) => SseFrame[];
  /** Flush a trailing frame that never got its blank-line terminator. */
  flush: () => SseFrame[];
}

const FRAME_SEPARATOR = /\r?\n\r?\n/;

const parseFrame = (raw: string): SseFrame | null => {
  let event = 'message';
  const dataLines: string[] = [];

  for (const line of raw.split(/\r?\n/)) {
    if (line.startsWith(':')) continue; // SSE comment / keepalive filler
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).replace(/^ /, ''));
    }
  }

  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join('\n') };
};

/**
 * Incremental SSE frame reader. Chunk boundaries fall wherever the network puts
 * them, so the buffer is kept across pushes and only complete frames (those
 * terminated by a blank line) are emitted.
 */
export const createSseFrameParser = (): SseFrameParser => {
  let buffer = '';

  const drain = (input: string, requireTerminator: boolean): SseFrame[] => {
    buffer += input;
    const frames: SseFrame[] = [];

    for (;;) {
      const match = FRAME_SEPARATOR.exec(buffer);
      if (!match) break;

      const raw = buffer.slice(0, match.index);
      buffer = buffer.slice(match.index + match[0].length);
      const frame = parseFrame(raw);
      if (frame) frames.push(frame);
    }

    if (!requireTerminator && buffer.trim().length > 0) {
      const frame = parseFrame(buffer);
      buffer = '';
      if (frame) frames.push(frame);
    }

    return frames;
  };

  return {
    push: (chunk) => drain(chunk, true),
    flush: () => drain('', false),
  };
};
